import { createFileRoute, notFound } from '@tanstack/react-router'
import { useLocale } from 'use-intl'

import { getTranslations } from '@/i18n/translator'
import { pageHead, SITE_URL } from '@/lib/seo'
import { breadcrumbSchema, homeBreadcrumbItem } from '@/lib/structured-data'
import { getResource } from '@/features/resources/data/resources'
import { ResourceDetail } from '@/features/resources/components/resource-detail'
import { ToolsNotFound } from '@/components/tools-not-found'

export const Route = createFileRoute('/$locale/resources/$slug')({
  loader: async ({ params }) => {
    const resource = getResource(params.slug)
    if (!resource) throw notFound()
    const t = await getTranslations({ locale: params.locale, namespace: 'resources' })
    return {
      slug: resource.slug,
      title: t(`items.${resource.slug}.title`),
      description: t(`items.${resource.slug}.subtitle`),
    }
  },
  // Leaf placement (like blog/legal): resources/$slug throws notFound, so its
  // ancestor resources/route.tsx (ResourcesLayout: logo + ToolsFooter) still
  // renders and ToolsNotFound shows inside its Outlet.
  notFoundComponent: () => <ToolsNotFound />,
  head: ({ loaderData, params }) => {
    const slug = loaderData?.slug ?? params.slug
    const path = `/resources/${slug}`
    const url = `${SITE_URL}/${params.locale}${path}`
    const title = loaderData?.title ?? ''
    const description = loaderData?.description ?? ''
    return pageHead({
      locale: params.locale,
      path,
      title,
      description,
      jsonLd: [
        breadcrumbSchema([
          homeBreadcrumbItem(params.locale),
          { name: title, url },
        ]),
      ],
    })
  },
  component: Page,
})

function Page() {
  const { slug } = Route.useParams()
  const locale = useLocale()
  const resource = getResource(slug)
  if (!resource) return null
  return <ResourceDetail resource={resource} locale={locale} />
}
